import type { Chain } from "viem"
import { deflate } from "pako"

export type SxBetData = {
  marketHash: string
  outcome: string
  percentageOdds: string
  outcomeOneName: string
  outcomeTwoName: string
  isMakerBettingOutcomeOne: boolean
  marketName: string
  chain: Chain | undefined
  txHash: string
  game: {
    sportId: number
    leagueLabel: string
    leagueSlug: string
    gameLabel: string
    sportsSlug: string
    sportsName: string
    betGroup: string
    marketType: string
    outcomeOneName: string
    outcomeTwoName: string
    providerMarketType: string
    gameTime: string
    fixtureId: string
    market: {
      marketHash: string
      baseToken: string
      bestOddsOutcomeOne: string
      bestOddsOutcomeTwo: string
      outcomeOneOdds: number
      outcomeTwoOdds: number
    }
  }
  odds: number
  uniqueId: string
  baseToken: string
  gameLabel: string
  betAmount: string
  totalOdds: number
  betToken: string
  // selectedCollateralSymbol: string
  live?: boolean
  allOdds?: { odd: number; takerBetAmount: number; orderHash: string }[]
}

interface SxDiscordEmbed {
  title: string
  description: string
  color: number
  fields: Array<{
    name: string
    value: string
    inline?: boolean
  }>
  footer: {
    text: string
    icon_url: string
  }
}

const createSxBetslipData = (bet: SxBetData) => {
  return {
    marketHash: bet.marketHash,
    outcome: bet.outcome,
    percentageOdds: bet.percentageOdds,
    outcomeOneName: bet.outcomeOneName,
    outcomeTwoName: bet.outcomeTwoName,
    isMakerBettingOutcomeOne: bet.isMakerBettingOutcomeOne,
    marketName: bet.marketName,
    chainId: bet.chain?.id,
    chainName: bet.chain?.name,
    txHash: bet.txHash,
    game: bet.game,
    odds: bet.odds,
    uniqueId: bet.uniqueId,
    baseToken: bet.baseToken,
    gameLabel: bet.gameLabel,
    betAmount: bet.betAmount,
    totalOdds: bet.totalOdds,
    betToken: bet.betToken,
    // selectedCollateralSymbol: bet.selectedCollateralSymbol,
    live: bet.live,
    allOdds: bet.allOdds,
  }
}

const createSxDiscordEmbed = (
  bet: SxBetData,
  redirectUrl: string,
  matchUrl: string
): SxDiscordEmbed => {
  try {
    const { game, betAmount, odds, betToken } = bet
    const { sportsName, leagueLabel, gameLabel } = game

    const embedResult = {
      title: `${sportsName} > International Tournaments > ${leagueLabel} ${bet.live ? "🔴 (LIVE)" : ""}`,
      description: `**[${gameLabel}](${matchUrl})**`,
      fields: [
        {
          name: "🎯 Market",
          value: `**${bet.marketName}**`,
          inline: true,
        },
        {
          name: "📌 Selection",
          value: `**${bet.outcome}**`,
          inline: true,
        },
        { name: "\u200B", value: "\u200B", inline: true },
        {
          name: "💰 Bet Amount",
          value: `${betAmount} ${betToken === "USDC" ? `${betToken} SX` : betToken}`,
          inline: true,
        },
        {
          name: "🎲 Odds",
          value: `${odds.toFixed(2)}`,
          inline: true,
        },
        { name: "\u200B", value: "\u200B", inline: true },
        // {
        //   name: "🆔 Transaction Hash",
        //   value: txHash
        //     ? `[${txHash.slice(0, 10)}...${txHash.slice(-8)}](https://beratrail.io/tx/${txHash})`
        //     : "N/A",
        //   inline: true,
        // },
        {
          name: "Network",
          value: bet.chain?.name || "Berachain",
          inline: true,
        },
        { name: "\u200B", value: "\u200B", inline: true },
        {
          name: "",
          value: `📨 [Copy bet](${redirectUrl})`,
          inline: false,
        },
        {
          name: "",
          value:
            "*Copy bet functionality only works for currently active markets*",
          inline: false,
        },
      ],
      color: 0x3498db, // Blue color to distinguish from Azuro and Overtime
      footer: {
        text: "DGbet",
        icon_url: "https://sportsbook.dgbet.fun/images/logo.webp",
      },
    }

    return embedResult
  } catch (error) {
    console.error("Error creating SX Discord embed:", error)
    console.error("Bet data:", JSON.stringify(bet, null, 2))

    // Return a fallback embed instead of throwing
    return {
      title: "SX Bet Placed",
      description: `Market: ${bet.game?.marketType} - ${bet.outcome}`,
      fields: [
        {
          name: "💰 Bet Amount",
          value: `${bet.betAmount} ${bet.betToken === "USDC" ? `${bet.betToken} SX` : bet.betToken}`,
          inline: true,
        },
        {
          name: "🎲 Odds",
          value: `${bet.odds?.toFixed(2)}`,
          inline: true,
        },
        {
          name: "🏆 Sport",
          value: bet.game?.sportsName,
          inline: false,
        },
      ],
      color: 0x3498db,
      footer: {
        text: "DGbet",
        icon_url: "https://sportsbook.dgbet.fun/images/logo.webp",
      },
    }
  }
}

const createSxComboDiscordEmbed = (
  bets: SxBetData[],
  redirectUrl: string
): SxDiscordEmbed => {
  try {
    const { betAmount, betToken, chain } =
      bets[0] || {}

    // Calculate total odds by multiplying all individual bet odds
    const calculatedTotalOdds = bets.reduce((totalOdds, bet) => {
      return totalOdds * bet.totalOdds
    }, 1)

    return {
      title: `SX COMBO BET (x${bets.length})`,
      description: "",
      fields: [
        {
          name: "💰 Bet Amount",
          value: `${betAmount || "0"} ${betToken || "USDC"} SX`,
          inline: true,
        },
        {
          name: "🎲 Total Odds",
          value: `${calculatedTotalOdds.toFixed(2)}`,
          inline: true,
        },
        { name: "\u200B", value: "\u200B", inline: true },
        {
          name: "Network",
          value: chain?.name as string,
          inline: false,
        },
        {
          name: "",
          value: `📨 [Copy bet](${redirectUrl})`,
          inline: false,
        },
        {
          name: "",
          value:
            "*Copy bet functionality only works for currently active markets*",
          inline: false,
        },
      ],
      color: 0x3498db, // Blue color to distinguish from Azuro and Overtime
      footer: {
        text: "DGbet",
        icon_url: "https://sportsbook.dgbet.fun/images/logo.webp",
      },
    }
  } catch (error) {
    console.error("Error creating SX Combo Discord embed:", error)
    throw error
  }
}

const compressSxData = (data: any, isSingle: boolean = true): string => {
  try {
    const compressedData = isSingle
      ? {
          mh: data.marketHash, // marketHash
          o: data.outcome, // outcome
          po: data.percentageOdds, // percentageOdds
          on: data.outcomeOneName, // outcomeOneName
          tn: data.outcomeTwoName, // outcomeTwoName
          mb: data.isMakerBettingOutcomeOne, // isMakerBettingOutcomeOne
          mn: data.marketName, // marketName
          c: data.chainId, // chainId
          tx: data.txHash, // txHash
          g: data.game, // game
          od: data.odds, // odds
          ui: data.uniqueId, // uniqueId
          bt: data.baseToken, // baseToken
          gl: data.gameLabel, // gameLabel
          ba: data.betAmount, // betAmount
          to: data.totalOdds, // totalOdds
          bto: data.betToken, // betToken
          // sc: data.selectedCollateralSymbol, // selectedCollateralSymbol
          l: data.live, // live
          ao: data.allOdds, // allOdds
        }
      : data.map((bet: any) => ({
          mh: bet.marketHash,
          o: bet.outcome,
          po: bet.percentageOdds,
          on: bet.outcomeOneName,
          tn: bet.outcomeTwoName,
          mb: bet.isMakerBettingOutcomeOne,
          mn: bet.marketName,
          c: bet.chainId,
          tx: bet.txHash,
          g: bet.game,
          od: bet.odds,
          ui: bet.uniqueId,
          bt: bet.baseToken,
          gl: bet.gameLabel,
          ba: bet.betAmount,
          to: bet.totalOdds,
          bto: bet.betToken,
          // sc: bet.selectedCollateralSymbol,
          l: bet.live,
          ao: bet.allOdds,
        }))

    const compressed = deflate(JSON.stringify(compressedData))

    // Convert to URL-safe base64
    return Buffer.from(compressed)
      .toString("base64")
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "")
  } catch (error) {
    console.error("Compression error:", error)
    throw error
  }
}

export {
  createSxBetslipData,
  createSxDiscordEmbed,
  createSxComboDiscordEmbed,
  compressSxData,
}

export type { SxDiscordEmbed }
